import { useState, useEffect, useCallback } from "react";
import {
  Pencil,
  Trash2,
  Plus,
  X,
  Check,
  Loader2,
  Download,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Checkbox } from "../ui/checkbox";
import { AuditSession } from "../../models/audit/auditSession";
import {
  useProcedureAttributes,
  AttributeRequestBody,
  AttributeType,
  MatchStrategy,
  stringMatchStrategies,
  numberMatchStrategies,
} from "../../hooks/useProcedureAttributes";
import { DropAllDocsConfirmDialog } from "./DropAllDocsConfirmDialog";
import { useHookCache } from "../../lib/hookCache";
import { analyticsKeyPrefix } from "../../lib/analyticsOwner";
import { useSessionAnalyticsOwner } from "../../hooks/audit/useSessionAnalyticsOwner";

interface ProcedureAttributesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  auditSession: AuditSession;
}

interface AttributeFormData {
  name: string;
  type: AttributeType;
  description: string;
  matchStrategy: MatchStrategy | "";
  matchedOn: boolean;
  sourceRowDisplay: boolean;
}

const DEFAULT_FORM_DATA: AttributeFormData = {
  name: "",
  type: "string",
  description: "",
  matchStrategy: "",
  matchedOn: false,
  sourceRowDisplay: false,
};

function getMatchStrategiesForType(type: AttributeType) {
  return type === "string" ? stringMatchStrategies : numberMatchStrategies;
}

function formatMatchStrategy(strategy: string | null): string {
  if (!strategy) return "-";
  return strategy
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatFieldType(fieldType: string | null): string {
  if (!fieldType) return "-";
  return fieldType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Local attribute type matching the procedure's attribute structure
type LocalAttribute = AuditSession["procedure"]["attributes"][number];

export function ProcedureAttributesModal({
  open,
  onOpenChange,
  auditSession,
}: ProcedureAttributesModalProps) {
  // Local state for attributes - avoids hard refresh
  const [localAttributes, setLocalAttributes] = useState<LocalAttribute[]>(
    auditSession.procedure.attributes,
  );
  // Track which rows have pending operations
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(
    new Set(),
  );
  // Track if we're creating a new attribute
  const [isCreating, setIsCreating] = useState(false);
  // Track if we're resetting all attributes
  const [isResetting, setIsResetting] = useState(false);

  const [editingAttributeId, setEditingAttributeId] = useState<string | null>(
    null,
  );
  const [editFormData, setEditFormData] =
    useState<AttributeFormData>(DEFAULT_FORM_DATA);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newFormData, setNewFormData] =
    useState<AttributeFormData>(DEFAULT_FORM_DATA);
  const [dropDocsDialogOpen, setDropDocsDialogOpen] = useState(false);
  const [isDroppingDocs, setIsDroppingDocs] = useState(false);
  const [isDroppingSamples, setIsDroppingSamples] = useState(false);

  const {
    createAttribute,
    updateAttribute,
    deleteAttribute,
    resetAttributes,
    dropSamples,
    dropAllDocs,
  } = useProcedureAttributes({
    procedureId: auditSession.procedure.id,
    sessionId: auditSession.id,
  });
  const analyticsOwner = useSessionAnalyticsOwner();
  const { invalidate } = useHookCache();

  // Sync local state when auditSession changes (e.g., from external refresh)
  useEffect(() => {
    setLocalAttributes(auditSession.procedure.attributes);
  }, [auditSession.procedure.attributes]);

  // Check if any documents have been uploaded
  const hasUploadedDocs = auditSession.documents.some(
    (doc) => doc.fetchableDocument !== null,
  );

  // Helper to add/remove from pending operations
  const addPending = useCallback((id: string) => {
    setPendingOperations((prev) => new Set(prev).add(id));
  }, []);

  const removePending = useCallback((id: string) => {
    setPendingOperations((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Handle modal close
  const handleClose = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setEditingAttributeId(null);
      setEditFormData(DEFAULT_FORM_DATA);
      setIsAddingNew(false);
      setNewFormData(DEFAULT_FORM_DATA);
      setPendingOperations(new Set());
    }
  }, [open]);

  const handleStartEdit = (attributeId: string) => {
    const attribute = localAttributes.find((a) => a.id === attributeId);
    if (!attribute) return;

    setEditFormData({
      name: attribute.name,
      type: attribute.type === "number" ? "number" : "string",
      description: attribute.description ?? "",
      matchStrategy: (attribute.matchStrategy ?? "") as MatchStrategy | "",
      matchedOn: (attribute.weight ?? 0) > 0,
      sourceRowDisplay: attribute.sourceRowDisplay,
    });
    setEditingAttributeId(attributeId);
  };

  const handleCancelEdit = () => {
    setEditingAttributeId(null);
    setEditFormData(DEFAULT_FORM_DATA);
  };

  const handleSaveEdit = async () => {
    if (!editingAttributeId) return;

    const data: AttributeRequestBody = {
      name: editFormData.name,
      type: editFormData.type,
      description: editFormData.description || null,
      match_strategy: editFormData.matchStrategy || null,
      weight: editFormData.matchedOn ? 1 : 0,
      source_row_display: editFormData.sourceRowDisplay,
    };

    addPending(editingAttributeId);
    const result = await updateAttribute(editingAttributeId, data);
    removePending(editingAttributeId);

    if (result) {
      // Update local state with the response
      setLocalAttributes((prev) =>
        prev.map((attr) =>
          attr.id === editingAttributeId
            ? {
                ...attr,
                name: result.name,
                type: result.type,
                description: result.description,
                matchStrategy: result.matchStrategy,
                fieldType: result.fieldType,
                weight: result.weight,
                sourceRowDisplay: result.sourceRowDisplay,
              }
            : attr,
        ),
      );
      setEditingAttributeId(null);
      setEditFormData(DEFAULT_FORM_DATA);
    }
  };

  const handleDelete = async (attributeId: string) => {
    addPending(attributeId);
    const success = await deleteAttribute(attributeId);
    removePending(attributeId);

    if (success) {
      // Remove from local state
      setLocalAttributes((prev) =>
        prev.filter((attr) => attr.id !== attributeId),
      );
    }
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewFormData(DEFAULT_FORM_DATA);
  };

  const handleSaveNew = async () => {
    const data: AttributeRequestBody = {
      name: newFormData.name,
      type: newFormData.type,
      description: newFormData.description || null,
      match_strategy: newFormData.matchStrategy || null,
      weight: newFormData.matchedOn ? 1 : 0,
      source_row_display: newFormData.sourceRowDisplay,
    };

    setIsCreating(true);
    const result = await createAttribute(data);
    setIsCreating(false);

    if (result) {
      // Add to local state
      const newAttr: LocalAttribute = {
        id: result.id,
        name: result.name,
        type: result.type,
        description: result.description,
        matchStrategy: result.matchStrategy,
        fieldType: result.fieldType,
        weight: result.weight,
        sourceRowDisplay: result.sourceRowDisplay,
        createdAt: result.createdAt,
        updatedAt: result.updatedAt,
      };
      setLocalAttributes((prev) => [...prev, newAttr]);
      setIsAddingNew(false);
      setNewFormData(DEFAULT_FORM_DATA);
    }
  };

  const handleResetAttributes = async () => {
    setIsResetting(true);
    const success = await resetAttributes();
    setIsResetting(false);

    if (success) {
      // Clear local state
      setLocalAttributes([]);
    }
  };

  const handleDropSamples = async () => {
    setIsDroppingSamples(true);
    const success = await dropSamples();
    setIsDroppingSamples(false);

    if (success) {
      // Close modal after successful drop - let page refresh handle state
      handleClose();
    }
  };

  const handleDownloadSamples = () => {
    const sourceRows = auditSession.sourceRows;
    const attributeIds = localAttributes.map((a) => a.id);
    const extraIds = Array.from(
      new Set(
        sourceRows.flatMap((row) =>
          Object.keys(row.data).filter((k) => !attributeIds.includes(k)),
        ),
      ),
    );
    const headers = [
      "Sample #",
      ...localAttributes.map((a) => a.name),
      ...extraIds,
    ];

    const escape = (value: unknown): string => {
      if (value === null || value === undefined) return "";
      // Strip BOM and zero-width chars that can leak in from Excel/CSV imports
      const str = String(value).replace(/[\uFEFF\u200B-\u200D]/g, "");
      if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };

    const rows = sourceRows.map((row) => {
      const cells = [
        row.sampleNumber ?? "",
        ...attributeIds.map((id) => row.data[id]),
        ...extraIds.map((id) => row.data[id]),
      ];
      return cells.map(escape).join(",");
    });

    const csv = [headers.map(escape).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${auditSession.procedure.name}-samples.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDropAllDocs = async () => {
    setIsDroppingDocs(true);
    const success = await dropAllDocs();
    setIsDroppingDocs(false);
    setDropDocsDialogOpen(false);

    // Dropping the documents drops the analytics input bindings that name
    // them server-side; bindings are cached per analytics owner, not per
    // session. The backend can answer 500 after committing the drop, so
    // refresh them either way.
    if (analyticsOwner) {
      void invalidate(analyticsKeyPrefix(analyticsOwner));
    }

    if (success) {
      // Close modal after successful drop - let page refresh handle state
      handleClose();
    }
  };

  const canSaveEdit =
    editFormData.name.trim().length > 0 && editFormData.type.length > 0;

  const canSaveNew =
    newFormData.name.trim().length > 0 && newFormData.type.length > 0;

  const renderAttributeRow = (attribute: LocalAttribute) => {
    const isEditing = editingAttributeId === attribute.id;
    const isPending = pendingOperations.has(attribute.id);

    if (isEditing) {
      return (
        <TableRow key={attribute.id} className={isPending ? "opacity-50" : ""}>
          <TableCell>
            <Input
              value={editFormData.name}
              onChange={(e) =>
                setEditFormData({ ...editFormData, name: e.target.value })
              }
              placeholder="Attribute name"
              className="h-8"
              disabled={isPending}
            />
          </TableCell>
          <TableCell>
            <Select
              value={editFormData.type}
              onValueChange={(value: AttributeType) => {
                setEditFormData({
                  ...editFormData,
                  type: value,
                  matchStrategy: "", // Reset match strategy when type changes
                });
              }}
              disabled={isPending}
            >
              <SelectTrigger className="h-8 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="string">String</SelectItem>
                <SelectItem value="number">Number</SelectItem>
              </SelectContent>
            </Select>
          </TableCell>
          <TableCell>
            <Select
              value={editFormData.matchStrategy}
              onValueChange={(value: MatchStrategy) =>
                setEditFormData({ ...editFormData, matchStrategy: value })
              }
              disabled={isPending}
            >
              <SelectTrigger className="h-8 w-32">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {getMatchStrategiesForType(editFormData.type).map(
                  (strategy) => (
                    <SelectItem key={strategy} value={strategy}>
                      {formatMatchStrategy(strategy)}
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
          </TableCell>
          <TableCell className="text-center">
            <Checkbox
              checked={editFormData.matchedOn}
              onCheckedChange={(checked) =>
                setEditFormData({
                  ...editFormData,
                  matchedOn: checked === true,
                })
              }
              disabled={isPending}
            />
          </TableCell>
          <TableCell className="text-center">
            <Checkbox
              checked={editFormData.sourceRowDisplay}
              onCheckedChange={(checked) =>
                setEditFormData({
                  ...editFormData,
                  sourceRowDisplay: checked === true,
                })
              }
              disabled={isPending}
            />
          </TableCell>
          <TableCell>
            <div className="flex gap-1">
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleSaveEdit}
                    disabled={!canSaveEdit}
                  >
                    <Check className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleCancelEdit}
                  >
                    <X className="h-4 w-4 text-gray-500" />
                  </Button>
                </>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow key={attribute.id} className={isPending ? "opacity-50" : ""}>
        <TableCell className="font-medium">{attribute.name}</TableCell>
        <TableCell>{attribute.type}</TableCell>
        <TableCell>{formatFieldType(attribute.fieldType)}</TableCell>
        <TableCell className="text-center">
          {(attribute.weight ?? 0) > 0 ? "Yes" : "No"}
        </TableCell>
        <TableCell className="text-center">
          {attribute.sourceRowDisplay ? "Yes" : "No"}
        </TableCell>
        <TableCell>
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
          ) : (
            <TooltipProvider>
              <div className="flex gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleStartEdit(attribute.id)}
                        disabled={hasUploadedDocs}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {hasUploadedDocs && (
                    <TooltipContent>
                      <p>Documents must be removed first</p>
                    </TooltipContent>
                  )}
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-red-500 hover:text-red-700"
                        onClick={() => handleDelete(attribute.id)}
                        disabled={hasUploadedDocs}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {hasUploadedDocs && (
                    <TooltipContent>
                      <p>Documents must be removed first</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
        </TableCell>
      </TableRow>
    );
  };

  const renderNewAttributeRow = () => {
    if (!isAddingNew) return null;

    return (
      <TableRow className={isCreating ? "opacity-50" : ""}>
        <TableCell>
          <Input
            value={newFormData.name}
            onChange={(e) =>
              setNewFormData({ ...newFormData, name: e.target.value })
            }
            placeholder="Attribute name"
            className="h-8"
            autoFocus
            disabled={isCreating}
          />
        </TableCell>
        <TableCell>
          <Select
            value={newFormData.type}
            onValueChange={(value: AttributeType) => {
              setNewFormData({
                ...newFormData,
                type: value,
                matchStrategy: "", // Reset match strategy when type changes
              });
            }}
            disabled={isCreating}
          >
            <SelectTrigger className="h-8 w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">String</SelectItem>
              <SelectItem value="number">Number</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Select
            value={newFormData.matchStrategy}
            onValueChange={(value: MatchStrategy) =>
              setNewFormData({ ...newFormData, matchStrategy: value })
            }
            disabled={isCreating}
          >
            <SelectTrigger className="h-8 w-32">
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {getMatchStrategiesForType(newFormData.type).map((strategy) => (
                <SelectItem key={strategy} value={strategy}>
                  {formatMatchStrategy(strategy)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell className="text-center">
          <Checkbox
            checked={newFormData.matchedOn}
            onCheckedChange={(checked) =>
              setNewFormData({
                ...newFormData,
                matchedOn: checked === true,
              })
            }
            disabled={isCreating}
          />
        </TableCell>
        <TableCell className="text-center">
          <Checkbox
            checked={newFormData.sourceRowDisplay}
            onCheckedChange={(checked) =>
              setNewFormData({
                ...newFormData,
                sourceRowDisplay: checked === true,
              })
            }
            disabled={isCreating}
          />
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            {isCreating ? (
              <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleSaveNew}
                  disabled={!canSaveNew}
                >
                  <Check className="h-4 w-4 text-green-600" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleCancelAdd}
                >
                  <X className="h-4 w-4 text-gray-500" />
                </Button>
              </>
            )}
          </div>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Sample Fields</DialogTitle>
            <DialogDescription>
              Configure fields for {auditSession.procedure.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadSamples}
                disabled={auditSession.sourceRows.length === 0}
              >
                <Download className="h-4 w-4 mr-1" />
                Download Samples
              </Button>
              {hasUploadedDocs ? (
                // When docs are uploaded, show Drop All Docs button
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDropDocsDialogOpen(true)}
                        disabled={isDroppingDocs}
                      >
                        {isDroppingDocs && (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        )}
                        Drop All Documents
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        All documents must be dropped before making changes to
                        sample attributes
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                // When no docs, show attribute management buttons
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDropSamples}
                    disabled={isDroppingSamples}
                  >
                    {isDroppingSamples && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Drop Samples
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetAttributes}
                    disabled={isResetting}
                  >
                    {isResetting && (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    )}
                    Reset Fields
                  </Button>

                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setIsAddingNew(true)}
                    disabled={isAddingNew}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Field
                  </Button>
                </>
              )}
            </div>

            {/* Attributes Table */}
            {localAttributes.length === 0 && !isAddingNew ? (
              <div className="text-center py-8 text-gray-500">
                No attributes defined. Click "Add Attribute" to create one.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead className="w-24">Type</TableHead>
                    <TableHead className="w-32">Field Type</TableHead>
                    <TableHead className="w-24 text-center">
                      Matched On
                    </TableHead>
                    <TableHead className="w-20 text-center">Display</TableHead>
                    <TableHead className="w-24">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {localAttributes.map(renderAttributeRow)}
                  {renderNewAttributeRow()}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DropAllDocsConfirmDialog
        open={dropDocsDialogOpen}
        onOpenChange={setDropDocsDialogOpen}
        isDeleting={isDroppingDocs}
        onConfirm={handleDropAllDocs}
      />
    </>
  );
}
